import React, { useState, useEffect } from 'react';
import {
  FileText,
  AlertCircle,
  CheckCircle,
  DollarSign,
  Download,
  Plus,
  RefreshCw,
  Search,
  ShieldAlert,
  UserCheck,
  CheckSquare,
  Wrench,
  Camera,
  Award,
  Clock,
  ChevronRight,
  ExternalLink,
  Building2
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  coreInsuranceApi,
  CoreClaimDto,
  AuthorizedGarageDto,
  CertifiedLossAdjusterDto,
  CoreRepairWorkOrderDto,
  ClaimInspectionEvidenceDto
} from '../api/coreInsuranceApi';

const GARAGE_STATUS_LABELS: Record<number, { label: string; color: string }> = {
  1: { label: 'Authorized', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  2: { label: 'Parts Procurement', color: 'bg-amber-100 text-amber-800 border-amber-200' },
  3: { label: 'Repairs In Progress', color: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
  4: { label: 'Post-Repair Inspection', color: 'bg-purple-100 text-purple-800 border-purple-200' },
  5: { label: 'Completed & Discharged', color: 'bg-emerald-100 text-emerald-800 border-emerald-200' }
};

export default function CoreClaims() {
  const [activeTab, setActiveTab] = useState<'claims' | 'garages'>('claims');

  // Claims Register State
  const [claims, setClaims] = useState<CoreClaimDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Lodge FNOL Modal
  const [showFnolModal, setShowFnolModal] = useState(false);
  const [lodging, setLodging] = useState(false);
  const [fnolForm, setFnolForm] = useState({
    policyId: '',
    dateOfLoss: new Date().toISOString().slice(0, 10),
    peril: 1, // Accidental Collision
    lossDescription: 'Frontal collision on M1 Highway, radiator and bonnet damaged',
    estimatedLossAmount: 4500000,
    claimantContactPhone: '+265 999 123 456'
  });

  // Surveyor Assessment Modal
  const [selectedClaim, setSelectedClaim] = useState<CoreClaimDto | null>(null);
  const [showAssessmentModal, setShowAssessmentModal] = useState(false);
  const [partsCost, setPartsCost] = useState(3000000);
  const [labourCost, setLabourCost] = useState(1200000);
  const [assessing, setAssessing] = useState(false);

  // Settlement & DV Modal
  const [showDvModal, setShowDvModal] = useState(false);
  const [disbursing, setDisbursing] = useState(false);
  const [signedByClaimant, setSignedByClaimant] = useState(true);

  // --- Garages & Work Orders State ---
  const [garages, setGarages] = useState<AuthorizedGarageDto[]>([]);
  const [adjusters, setAdjusters] = useState<CertifiedLossAdjusterDto[]>([]);
  const [workOrders, setWorkOrders] = useState<CoreRepairWorkOrderDto[]>([]);
  const [loadingGarages, setLoadingGarages] = useState(false);

  // Issue Work Order Modal
  const [showWorkOrderModal, setShowWorkOrderModal] = useState(false);
  const [issuingWo, setIssuingWo] = useState(false);
  const [woForm, setWoForm] = useState({
    claimId: '',
    claimNumber: '',
    policyNumber: '',
    registrationNumber: '',
    vehicleMakeModel: '',
    garageId: '',
    assessorId: '',
    authorizedPartsCost: 2500000,
    authorizedLabourCost: 800000,
    authorizedPaintMaterialsCost: 450000,
    estimatedDaysToComplete: 14,
    authorizedBy: 'Senior Claims Engineer',
    remarks: 'Authorized OEM spare parts replacement. Retain all damaged parts for salvage audit.'
  });

  // Progress Update Modal
  const [selectedWorkOrder, setSelectedWorkOrder] = useState<CoreRepairWorkOrderDto | null>(null);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [newProgressStatus, setNewProgressStatus] = useState<number>(3);
  const [progressNote, setProgressNote] = useState('Panel beating & chassis alignment completed; entering spray booth.');
  const [updatingProgress, setUpdatingProgress] = useState(false);

  // QA Certification Modal
  const [showCertifyModal, setShowCertifyModal] = useState(false);
  const [qaCertNumber, setQaCertNumber] = useState(`QA-MW-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`);
  const [qaRemarks, setQaRemarks] = useState('Post-repair laser chassis alignment 100% within tolerance. Paint thickness OEM match.');
  const [certifying, setCertifying] = useState(false);

  useEffect(() => {
    fetchClaims();
    fetchGarageData();
  }, []);

  const fetchClaims = async () => {
    setLoading(true);
    try {
      const data = await coreInsuranceApi.getClaims();
      setClaims(data);
    } catch {
      // Non-blocking in dev
    } finally {
      setLoading(false);
    }
  };

  const fetchGarageData = async () => {
    setLoadingGarages(true);
    try {
      const [gData, aData, wData] = await Promise.all([
        coreInsuranceApi.getApprovedGarages(),
        coreInsuranceApi.getCertifiedAdjusters(),
        coreInsuranceApi.getAllWorkOrders()
      ]);
      setGarages(gData);
      setAdjusters(aData);
      setWorkOrders(wData);
      if (gData.length > 0) {
        setWoForm(prev => ({ ...prev, garageId: gData[0].id }));
      }
      if (aData.length > 0) {
        setWoForm(prev => ({ ...prev, assessorId: aData[0].id }));
      }
    } catch {
      // Non-blocking in dev
    } finally {
      setLoadingGarages(false);
    }
  };

  const handleLodgeFnol = async (e: React.FormEvent) => {
    e.preventDefault();
    setLodging(true);
    try {
      await coreInsuranceApi.lodgeFnol({
        ...fnolForm,
        dateOfLoss: new Date(fnolForm.dateOfLoss).toISOString()
      });
      toast.success('FNOL Claim lodged successfully! Initial OSLR Reserve posted.');
      setShowFnolModal(false);
      fetchClaims();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to lodge FNOL claim.');
    } finally {
      setLodging(false);
    }
  };

  const handleOpenAssessment = (claim: CoreClaimDto) => {
    setSelectedClaim(claim);
    setPartsCost(Math.round((claim.currentReserveAmount || 3000000) * 0.7));
    setLabourCost(Math.round((claim.currentReserveAmount || 3000000) * 0.3));
    setShowAssessmentModal(true);
  };

  const handleSubmitAssessment = async () => {
    if (!selectedClaim) return;
    setAssessing(true);
    try {
      await coreInsuranceApi.submitAssessment(selectedClaim.id, {
        estimatedPartsCost: partsCost,
        estimatedLabourCost: labourCost,
        paintAndMaterialsCost: 350000,
        towingAndStorageFee: 150000,
        bettermentPercentage: 15.0,
        isTotalLoss: false,
        preAccidentMarketValue: 25000000,
        estimatedSalvageValue: 0,
        assessorRemarks: 'OEM genuine components approved. Body structure fully recoverable.'
      });
      toast.success('Surveyor assessment submitted. Net payable loss calculated!');
      setShowAssessmentModal(false);
      fetchClaims();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Assessment submission failed.');
    } finally {
      setAssessing(false);
    }
  };

  const handleOpenDvModal = (claim: CoreClaimDto) => {
    setSelectedClaim(claim);
    setShowDvModal(true);
  };

  const handleIssueAndSignDv = async () => {
    if (!selectedClaim) return;
    try {
      await coreInsuranceApi.issueDischargeVoucher(selectedClaim.id, {
        issuedBy: 'Claims Manager - Xtrem Head Office'
      });
      toast.success('Legal Discharge Voucher issued and signed by claimant!');
      setShowDvModal(false);
      fetchClaims();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Discharge voucher issuance failed.');
    }
  };

  const handleDisburseSettlement = async (claim: CoreClaimDto) => {
    setDisbursing(true);
    try {
      await coreInsuranceApi.disbursePayment(claim.id, {
        disbursedAmount: claim.netPayableAmount || claim.currentReserveAmount,
        payeeName: claim.policyholderName,
        paymentChannel: 'National Bank of Malawi Direct Credit',
        bankTransactionReference: `NBM-EFT-${Date.now().toString().slice(-6)}`
      });
      toast.success('Payment disbursed! OSLR Reserve fully extinguished.');
      fetchClaims();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Payment disbursement failed.');
    } finally {
      setDisbursing(false);
    }
  };

  const handleOpenWorkOrderForClaim = (claim: CoreClaimDto) => {
    setWoForm({
      claimId: claim.id,
      claimNumber: claim.claimNumber,
      policyNumber: claim.policyNumber,
      registrationNumber: claim.registrationNumber || 'MW-REG-01',
      vehicleMakeModel: `${claim.make || 'Toyota'} ${claim.model || 'Motor Vehicle'}`,
      garageId: garages[0]?.id || '',
      assessorId: adjusters[0]?.id || '',
      authorizedPartsCost: Math.round((claim.netPayableAmount || 2500000) * 0.7),
      authorizedLabourCost: Math.round((claim.netPayableAmount || 2500000) * 0.2),
      authorizedPaintMaterialsCost: Math.round((claim.netPayableAmount || 2500000) * 0.1),
      estimatedDaysToComplete: 14,
      authorizedBy: 'Senior Claims Engineer',
      remarks: 'Authorized repair under approved surveyor estimate. Genuine OEM parts mandatory.'
    });
    setShowWorkOrderModal(true);
  };

  const handleIssueWorkOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    setIssuingWo(true);
    try {
      const wo = await coreInsuranceApi.issueWorkOrder(woForm);
      toast.success(`Work Order ${wo.workOrderNumber} issued to ${wo.garageName}!`);
      setShowWorkOrderModal(false);
      fetchGarageData();
      setActiveTab('garages');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to issue work order');
    } finally {
      setIssuingWo(false);
    }
  };

  const handleUpdateProgress = async () => {
    if (!selectedWorkOrder) return;
    setUpdatingProgress(true);
    try {
      await coreInsuranceApi.updateWorkOrderProgress(selectedWorkOrder.id, newProgressStatus, progressNote);
      toast.success('Repair progress milestone logged successfully!');
      setShowProgressModal(false);
      fetchGarageData();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to update progress');
    } finally {
      setUpdatingProgress(false);
    }
  };

  const handleCertifyCompletion = async () => {
    if (!selectedWorkOrder) return;
    setCertifying(true);
    try {
      await coreInsuranceApi.certifyWorkOrder(selectedWorkOrder.id, qaCertNumber, qaRemarks);
      toast.success('Quality Assurance Pass certificate recorded & work order discharged!');
      setShowCertifyModal(false);
      fetchGarageData();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Certification failed');
    } finally {
      setCertifying(false);
    }
  };

  const filtered = claims.filter(c =>
    c.claimNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.policyNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.policyholderName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white dark:bg-slate-900 p-6 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-sm">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-400 border border-red-200 dark:border-red-900">
              Claims Subsystem
            </span>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-900">
              Approved Garages & Surveyors
            </span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <ShieldAlert className="text-red-600 dark:text-red-400" />
            Core Claims & Garage Work Order Management
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            FNOL intake, OSLR dynamic reserving, loss adjusters, approved garage repair work orders, and settlement discharge.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex rounded-xl bg-gray-100 dark:bg-slate-800 p-1">
            <button
              onClick={() => setActiveTab('claims')}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${
                activeTab === 'claims'
                  ? 'bg-white dark:bg-slate-900 text-red-600 dark:text-red-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900'
              }`}
            >
              Claims Register ({claims.length})
            </button>
            <button
              onClick={() => setActiveTab('garages')}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${
                activeTab === 'garages'
                  ? 'bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900'
              }`}
            >
              Garages & Work Orders ({workOrders.length})
            </button>
          </div>

          <button
            onClick={() => setShowFnolModal(true)}
            className="flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-3 py-2 rounded-lg shadow-sm transition"
          >
            <Plus size={16} /> Lodge FNOL
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl p-4 shadow-sm">
          <span className="text-xs font-semibold text-gray-400 uppercase">Total Claims Registered</span>
          <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{claims.length}</div>
          <span className="text-xs text-blue-500 font-medium">Claims lifetime registry</span>
        </div>

        <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl p-4 shadow-sm">
          <span className="text-xs font-semibold text-gray-400 uppercase">Outstanding OSLR Reserves</span>
          <div className="text-2xl font-bold text-amber-600 dark:text-amber-400 mt-1">
            MWK {(claims.reduce((acc, c) => acc + (c.currentReserveAmount || 0), 0) / 1000000).toFixed(2)}M
          </div>
          <span className="text-xs text-amber-600 font-medium">Active solvency reserve liability</span>
        </div>

        <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl p-4 shadow-sm">
          <span className="text-xs font-semibold text-gray-400 uppercase">Approved Panel Beaters</span>
          <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400 mt-1">
            {garages.length} <span className="text-xs font-normal text-gray-400">facilities</span>
          </div>
          <span className="text-xs text-indigo-500 font-medium">Toyota Malawi, CFAO, APL</span>
        </div>

        <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl p-4 shadow-sm">
          <span className="text-xs font-semibold text-gray-400 uppercase">Active Repair Work Orders</span>
          <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">
            {workOrders.length} <span className="text-xs font-normal text-gray-400">authorized</span>
          </div>
          <span className="text-xs text-emerald-500 font-medium">Live garage repair pipeline</span>
        </div>
      </div>

      {/* TAB 1: CLAIMS REGISTER */}
      {activeTab === 'claims' && (
        <div className="space-y-4">
          {/* Search Bar */}
          <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-gray-200 dark:border-slate-800 flex items-center justify-between">
            <div className="relative w-full max-w-md">
              <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search by claim #, policy #, or policyholder..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-slate-800 border rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            <button
              onClick={fetchClaims}
              className="p-2 bg-gray-50 dark:bg-slate-800 border rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 transition"
              title="Refresh"
            >
              <RefreshCw size={16} />
            </button>
          </div>

          {/* Claims Table */}
          <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-gray-50 dark:bg-slate-800/60 text-gray-600 dark:text-gray-300 font-semibold border-b">
                  <tr>
                    <th className="py-3.5 px-4">Claim # & Policy</th>
                    <th className="py-3.5 px-4">Policyholder & Date</th>
                    <th className="py-3.5 px-4">Peril & Details</th>
                    <th className="py-3.5 px-4">OSLR Reserve</th>
                    <th className="py-3.5 px-4">Net Assessed Loss</th>
                    <th className="py-3.5 px-4">Lifecycle Status</th>
                    <th className="py-3.5 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-slate-800">
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-gray-400">
                        <RefreshCw className="animate-spin inline mr-2" size={18} /> Loading claims register...
                      </td>
                    </tr>
                  ) : filtered.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-gray-400">
                        No claims registered. Click "Lodge FNOL" to report a loss.
                      </td>
                    </tr>
                  ) : (
                    filtered.map((claim) => (
                      <tr key={claim.id} className="hover:bg-gray-50/70 dark:hover:bg-slate-800/40 transition">
                        <td className="py-3 px-4">
                          <div className="font-mono font-bold text-red-600 dark:text-red-400">{claim.claimNumber}</div>
                          <div className="text-[11px] text-gray-500 font-mono">{claim.policyNumber}</div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="font-medium text-gray-900 dark:text-white">{claim.policyholderName}</div>
                          <div className="text-[11px] text-gray-400">{new Date(claim.dateOfLoss).toLocaleDateString()}</div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="font-semibold text-gray-800 dark:text-gray-200">{claim.peril}</div>
                          <div className="text-[11px] text-gray-500 max-w-xs truncate">{claim.lossDescription}</div>
                        </td>
                        <td className="py-3 px-4 font-mono font-bold text-amber-600 dark:text-amber-400">
                          MWK {claim.currentReserveAmount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </td>
                        <td className="py-3 px-4 font-mono font-semibold text-gray-800 dark:text-gray-200">
                          {claim.netPayableAmount
                            ? `MWK ${claim.netPayableAmount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
                            : 'Pending Survey'}
                        </td>
                        <td className="py-3 px-4">
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300">
                            {claim.status}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            {/* Surveyor Assessment button */}
                            <button
                              onClick={() => handleOpenAssessment(claim)}
                              className="px-2 py-1 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded text-[11px] font-semibold transition"
                              title="Surveyor Assessment"
                            >
                              Survey
                            </button>

                            {/* Issue Garage Work Order button */}
                            <button
                              onClick={() => handleOpenWorkOrderForClaim(claim)}
                              className="px-2 py-1 bg-amber-50 text-amber-800 hover:bg-amber-100 rounded text-[11px] font-semibold flex items-center gap-1 transition"
                              title="Authorize Repair Garage Work Order"
                            >
                              <Wrench size={12} /> Work Order
                            </button>

                            {/* Discharge Voucher */}
                            <button
                              onClick={() => handleOpenDvModal(claim)}
                              className="px-2 py-1 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded text-[11px] font-semibold transition"
                              title="Issue Discharge Voucher"
                            >
                              DV
                            </button>

                            {/* Settlement Disbursement */}
                            <button
                              onClick={() => handleDisburseSettlement(claim)}
                              className="px-2 py-1 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded text-[11px] font-semibold transition"
                              title="Disburse Settlement"
                            >
                              Pay
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: AUTHORIZED GARAGES & WORK ORDERS */}
      {activeTab === 'garages' && (
        <div className="space-y-6">
          {/* Approved Facilities & Adjusters Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Approved Garages Card */}
            <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl p-5 shadow-sm">
              <div className="flex items-center justify-between pb-3 border-b border-gray-100 dark:border-slate-800 mb-3">
                <div className="flex items-center gap-2">
                  <Building2 className="text-indigo-600 dark:text-indigo-400" size={18} />
                  <h3 className="font-bold text-gray-900 dark:text-white text-sm">Approved Partner Repair Facilities</h3>
                </div>
                <span className="text-xs text-indigo-600 font-semibold">{garages.length} Active Centers</span>
              </div>

              <div className="space-y-2.5">
                {garages.map(g => (
                  <div key={g.id} className="p-3 bg-gray-50 dark:bg-slate-800/60 rounded-xl flex items-center justify-between text-xs">
                    <div>
                      <div className="font-bold text-gray-900 dark:text-white">{g.name}</div>
                      <div className="text-gray-500 text-[11px]">{g.physicalAddress}</div>
                      <div className="text-gray-400 text-[10px] mt-0.5">Manager: {g.contactPerson} ({g.phone})</div>
                    </div>
                    <div className="text-right">
                      <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800">
                        {g.ratingScore} ★ Rating
                      </span>
                      <div className="text-[11px] text-gray-500 mt-1">{g.activeJobsCount} active job(s)</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Certified Adjusters Card */}
            <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl p-5 shadow-sm">
              <div className="flex items-center justify-between pb-3 border-b border-gray-100 dark:border-slate-800 mb-3">
                <div className="flex items-center gap-2">
                  <Award className="text-amber-600 dark:text-amber-400" size={18} />
                  <h3 className="font-bold text-gray-900 dark:text-white text-sm">Certified Independent Motor Surveyors</h3>
                </div>
                <span className="text-xs text-amber-600 font-semibold">{adjusters.length} Certified Adjusters</span>
              </div>

              <div className="space-y-2.5">
                {adjusters.map(a => (
                  <div key={a.id} className="p-3 bg-gray-50 dark:bg-slate-800/60 rounded-xl flex items-center justify-between text-xs">
                    <div>
                      <div className="font-bold text-gray-900 dark:text-white">{a.fullName}</div>
                      <div className="text-indigo-600 font-medium text-[11px]">{a.firmName}</div>
                      <div className="text-gray-400 text-[10px] mt-0.5">Specialization: {a.specialization}</div>
                    </div>
                    <div className="text-right">
                      <span className="font-mono text-[10px] bg-amber-50 text-amber-800 px-2 py-0.5 rounded border border-amber-200">
                        {a.licenseNumber}
                      </span>
                      <div className="text-[10px] text-gray-400 mt-1">Limit: MWK {(a.maxAssessmentLimit / 1000000).toFixed(0)}M</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Active Work Orders Table */}
          <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl shadow-sm overflow-hidden">
            <div className="p-5 border-b border-gray-100 dark:border-slate-800 flex justify-between items-center">
              <div>
                <h3 className="font-bold text-gray-900 dark:text-white text-base">Active Repair Work Orders</h3>
                <p className="text-xs text-gray-500">Live panel beating, parts procurement, and quality assurance authorizations</p>
              </div>

              <button
                onClick={fetchGarageData}
                className="p-2 text-gray-500 hover:text-indigo-600 rounded-lg hover:bg-gray-50 transition"
              >
                <RefreshCw size={16} />
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-gray-50 dark:bg-slate-800 text-gray-600 dark:text-gray-300 font-semibold border-b">
                  <tr>
                    <th className="py-3 px-4">Work Order #</th>
                    <th className="py-3 px-4">Claim & Policy</th>
                    <th className="py-3 px-4">Damaged Vehicle</th>
                    <th className="py-3 px-4">Assigned Facility</th>
                    <th className="py-3 px-4">Authorized Amount</th>
                    <th className="py-3 px-4">Target Date</th>
                    <th className="py-3 px-4">Job Status</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                  {workOrders.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="py-8 text-center text-gray-400">
                        No repair work orders issued yet. Select an assessed claim from the Claims Register and click "Work Order".
                      </td>
                    </tr>
                  ) : (
                    workOrders.map(wo => {
                      const statusMeta = GARAGE_STATUS_LABELS[wo.status] || { label: 'In Progress', color: 'bg-gray-100 text-gray-700' };
                      return (
                        <tr key={wo.id} className="hover:bg-gray-50/70 dark:hover:bg-slate-800/40">
                          <td className="py-3 px-4 font-mono font-bold text-indigo-600 dark:text-indigo-400">
                            {wo.workOrderNumber}
                          </td>
                          <td className="py-3 px-4">
                            <div className="font-mono font-semibold text-gray-900 dark:text-white">{wo.claimNumber}</div>
                            <div className="text-[10px] text-gray-400 font-mono">{wo.policyNumber}</div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="font-semibold text-gray-900 dark:text-white">{wo.vehicleMakeModel}</div>
                            <div className="text-[10px] font-mono text-gray-500">{wo.registrationNumber}</div>
                          </td>
                          <td className="py-3 px-4 font-medium text-gray-800 dark:text-gray-200">
                            {wo.garageName}
                          </td>
                          <td className="py-3 px-4 font-mono font-bold text-emerald-600 dark:text-emerald-400">
                            MWK {wo.totalAuthorizedAmount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </td>
                          <td className="py-3 px-4 text-gray-500">
                            {new Date(wo.estimatedCompletionDate).toLocaleDateString()}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${statusMeta.color}`}>
                              {statusMeta.label}
                            </span>
                            {wo.qualityPassCertificateNumber && (
                              <div className="text-[10px] text-emerald-600 font-mono font-bold mt-0.5">
                                Pass: {wo.qualityPassCertificateNumber}
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              {/* Download Work Order PDF */}
                              <button
                                onClick={() => coreInsuranceApi.downloadWorkOrderPdf(wo.id, wo.workOrderNumber)}
                                className="px-2 py-1 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded text-[11px] font-semibold flex items-center gap-1 transition"
                                title="Download PDF Work Order"
                              >
                                <Download size={12} /> PDF
                              </button>

                              {/* Update Milestone */}
                              {wo.status !== 5 && (
                                <button
                                  onClick={() => {
                                    setSelectedWorkOrder(wo);
                                    setNewProgressStatus(wo.status === 1 ? 2 : 3);
                                    setShowProgressModal(true);
                                  }}
                                  className="px-2 py-1 bg-amber-50 text-amber-800 hover:bg-amber-100 rounded text-[11px] font-semibold transition"
                                >
                                  Update
                                </button>
                              )}

                              {/* QA Certify */}
                              {wo.status !== 5 && (
                                <button
                                  onClick={() => {
                                    setSelectedWorkOrder(wo);
                                    setShowCertifyModal(true);
                                  }}
                                  className="px-2 py-1 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded text-[11px] font-semibold flex items-center gap-1 transition"
                                >
                                  <Award size={12} /> Certify
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* LODGE FNOL MODAL */}
      {showFnolModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-lg w-full border border-gray-200 dark:border-slate-800 shadow-2xl p-6 space-y-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <ShieldAlert className="text-red-600" />
              Lodge New First Notice of Loss (FNOL)
            </h3>
            <p className="text-xs text-gray-500">
              Automated motor incident registration, active policy verification, and OSLR case reserving.
            </p>

            <form onSubmit={handleLodgeFnol} className="space-y-3 text-xs">
              <div>
                <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">Policy Identifier (or Policy #)</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. POL-MOT-2026-XXXX or Policy GUID"
                  value={fnolForm.policyId}
                  onChange={(e) => setFnolForm({ ...fnolForm, policyId: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg bg-gray-50 dark:bg-slate-800 text-gray-900 dark:text-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">Date of Loss</label>
                  <input
                    type="date"
                    required
                    value={fnolForm.dateOfLoss}
                    onChange={(e) => setFnolForm({ ...fnolForm, dateOfLoss: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg bg-gray-50 dark:bg-slate-800 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">Peril Incurred</label>
                  <select
                    value={fnolForm.peril}
                    onChange={(e) => setFnolForm({ ...fnolForm, peril: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg bg-gray-50 dark:bg-slate-800 text-gray-900 dark:text-white"
                  >
                    <option value={1}>Accidental Collision</option>
                    <option value={2}>Total Theft</option>
                    <option value={3}>Partial Theft</option>
                    <option value={4}>Fire Damage</option>
                    <option value={7}>Windscreen Breakage</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">Circumstances of Loss</label>
                <textarea
                  rows={3}
                  required
                  value={fnolForm.lossDescription}
                  onChange={(e) => setFnolForm({ ...fnolForm, lossDescription: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg bg-gray-50 dark:bg-slate-800 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">Initial Estimated Loss (MWK)</label>
                <input
                  type="number"
                  required
                  value={fnolForm.estimatedLossAmount}
                  onChange={(e) => setFnolForm({ ...fnolForm, estimatedLossAmount: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border rounded-lg bg-gray-50 dark:bg-slate-800 text-gray-900 dark:text-white"
                />
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t">
                <button
                  type="button"
                  onClick={() => setShowFnolModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={lodging}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg shadow-sm"
                >
                  {lodging ? 'Lodging...' : 'Register Claim'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ISSUE REPAIR WORK ORDER MODAL */}
      {showWorkOrderModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-xl w-full border border-gray-200 dark:border-slate-800 shadow-2xl p-6 space-y-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Wrench className="text-indigo-600" />
              Issue Garage Repair Work Order
            </h3>
            <p className="text-xs text-gray-500">
              Dispatches formal authorization and repair sum limit to certified panel beater for Claim {woForm.claimNumber}.
            </p>

            <form onSubmit={handleIssueWorkOrder} className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">Authorized Repair Garage</label>
                  <select
                    value={woForm.garageId}
                    onChange={(e) => setWoForm({ ...woForm, garageId: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg bg-gray-50 dark:bg-slate-800 text-gray-900 dark:text-white"
                  >
                    {garages.map(g => (
                      <option key={g.id} value={g.id}>{g.name} ({g.city})</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">Loss Adjuster / Surveyor</label>
                  <select
                    value={woForm.assessorId}
                    onChange={(e) => setWoForm({ ...woForm, assessorId: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg bg-gray-50 dark:bg-slate-800 text-gray-900 dark:text-white"
                  >
                    {adjusters.map(a => (
                      <option key={a.id} value={a.id}>{a.fullName} ({a.firmName})</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">Approved Parts (MWK)</label>
                  <input
                    type="number"
                    value={woForm.authorizedPartsCost}
                    onChange={(e) => setWoForm({ ...woForm, authorizedPartsCost: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border rounded-lg bg-gray-50 dark:bg-slate-800 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">Specialist Labour (MWK)</label>
                  <input
                    type="number"
                    value={woForm.authorizedLabourCost}
                    onChange={(e) => setWoForm({ ...woForm, authorizedLabourCost: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border rounded-lg bg-gray-50 dark:bg-slate-800 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">Paint & Consumables (MWK)</label>
                  <input
                    type="number"
                    value={woForm.authorizedPaintMaterialsCost}
                    onChange={(e) => setWoForm({ ...woForm, authorizedPaintMaterialsCost: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border rounded-lg bg-gray-50 dark:bg-slate-800 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              <div className="p-3 bg-indigo-50 dark:bg-indigo-950/40 rounded-xl flex justify-between items-center text-xs">
                <span className="font-semibold text-indigo-900 dark:text-indigo-300">Total Authorized Work Order Sum:</span>
                <span className="font-bold text-indigo-700 dark:text-indigo-400 text-sm">
                  MWK {(woForm.authorizedPartsCost + woForm.authorizedLabourCost + woForm.authorizedPaintMaterialsCost).toLocaleString()}
                </span>
              </div>

              <div>
                <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">Authorizing Officer Notes & Directives</label>
                <textarea
                  rows={2}
                  value={woForm.remarks}
                  onChange={(e) => setWoForm({ ...woForm, remarks: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg bg-gray-50 dark:bg-slate-800 text-gray-900 dark:text-white"
                />
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t">
                <button
                  type="button"
                  onClick={() => setShowWorkOrderModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={issuingWo}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg shadow-sm"
                >
                  {issuingWo ? 'Dispatching...' : 'Dispatch Work Order'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* UPDATE PROGRESS MODAL */}
      {showProgressModal && selectedWorkOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-md w-full border border-gray-200 dark:border-slate-800 shadow-2xl p-6 space-y-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Clock className="text-amber-600" />
              Update Repair Milestone ({selectedWorkOrder.workOrderNumber})
            </h3>

            <div className="space-y-3 text-xs">
              <div>
                <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">Next Repair Stage</label>
                <select
                  value={newProgressStatus}
                  onChange={(e) => setNewProgressStatus(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border rounded-lg bg-gray-50 dark:bg-slate-800 text-gray-900 dark:text-white"
                >
                  <option value={2}>Parts Procurement</option>
                  <option value={3}>Repairs In Progress</option>
                  <option value={4}>Post-Repair Inspection Pending</option>
                </select>
              </div>

              <div>
                <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">Workshop Manager / Technician Notes</label>
                <textarea
                  rows={3}
                  value={progressNote}
                  onChange={(e) => setProgressNote(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg bg-gray-50 dark:bg-slate-800 text-gray-900 dark:text-white"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-3 border-t">
              <button
                onClick={() => setShowProgressModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-900 text-xs"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateProgress}
                disabled={updatingProgress}
                className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-lg text-xs"
              >
                {updatingProgress ? 'Updating...' : 'Save Milestone'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QA CERTIFY MODAL */}
      {showCertifyModal && selectedWorkOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-md w-full border border-gray-200 dark:border-slate-800 shadow-2xl p-6 space-y-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Award className="text-emerald-600" />
              Post-Repair Quality Assurance Pass
            </h3>
            <p className="text-xs text-gray-500">
              Certifies vehicle repairs meet OEM safety specifications and authorizes release to policyholder.
            </p>

            <div className="space-y-3 text-xs">
              <div>
                <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">Quality Certificate #</label>
                <input
                  type="text"
                  value={qaCertNumber}
                  onChange={(e) => setQaCertNumber(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg bg-gray-50 dark:bg-slate-800 font-mono text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">Surveyor Final Inspection Remarks</label>
                <textarea
                  rows={3}
                  value={qaRemarks}
                  onChange={(e) => setQaRemarks(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg bg-gray-50 dark:bg-slate-800 text-gray-900 dark:text-white"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-3 border-t">
              <button
                onClick={() => setShowCertifyModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-900 text-xs"
              >
                Cancel
              </button>
              <button
                onClick={handleCertifyCompletion}
                disabled={certifying}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg text-xs"
              >
                {certifying ? 'Certifying...' : 'Record QA Pass & Release'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SURVEYOR ASSESSMENT MODAL */}
      {showAssessmentModal && selectedClaim && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-md w-full border border-gray-200 dark:border-slate-800 shadow-2xl p-6 space-y-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <UserCheck className="text-indigo-600" />
              Submit Surveyor Assessment Report
            </h3>
            <p className="text-xs text-gray-500">
              Applies statutory betterment depreciation and deductible excess buyback waivers.
            </p>

            <div className="space-y-3 text-xs">
              <div>
                <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">Estimated OEM Parts Cost (MWK)</label>
                <input
                  type="number"
                  value={partsCost}
                  onChange={(e) => setPartsCost(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border rounded-lg bg-gray-50 dark:bg-slate-800 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">Estimated Specialist Labour (MWK)</label>
                <input
                  type="number"
                  value={labourCost}
                  onChange={(e) => setLabourCost(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border rounded-lg bg-gray-50 dark:bg-slate-800 text-gray-900 dark:text-white"
                />
              </div>

              <div className="p-3 bg-gray-50 dark:bg-slate-800/80 rounded-xl space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-500">Gross Assessed:</span>
                  <span className="font-bold">MWK {(partsCost + labourCost + 500000).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-red-500">
                  <span>Betterment (15% on parts):</span>
                  <span>-MWK {(partsCost * 0.15).toLocaleString()}</span>
                </div>
                <div className="flex justify-between font-bold text-emerald-600 pt-1 border-t">
                  <span>Net Estimated Loss:</span>
                  <span>MWK {(partsCost * 0.85 + labourCost + 500000).toLocaleString()}</span>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-3 border-t">
              <button
                onClick={() => setShowAssessmentModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-900 text-xs"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitAssessment}
                disabled={assessing}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg text-xs shadow-sm"
              >
                {assessing ? 'Submitting...' : 'Submit Assessment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DISCHARGE VOUCHER MODAL */}
      {showDvModal && selectedClaim && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-md w-full border border-gray-200 dark:border-slate-800 shadow-2xl p-6 space-y-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <CheckSquare className="text-emerald-600" />
              Discharge Voucher Legal Release
            </h3>
            <p className="text-xs text-gray-500">
              Generates legal full and final discharge voucher for settlement of MWK {selectedClaim.netPayableAmount?.toLocaleString()}.
            </p>

            <div className="space-y-3 text-xs">
              <label className="flex items-center gap-2 p-3 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 rounded-xl cursor-pointer">
                <input
                  type="checkbox"
                  checked={signedByClaimant}
                  onChange={(e) => setSignedByClaimant(e.target.checked)}
                  className="rounded text-emerald-600"
                />
                <span className="font-semibold text-emerald-900 dark:text-emerald-300">
                  Claimant Digital Signature Verified & Sealed
                </span>
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-3 border-t">
              <button
                onClick={() => setShowDvModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-900 text-xs"
              >
                Cancel
              </button>
              <button
                onClick={handleIssueAndSignDv}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg text-xs shadow-sm"
              >
                Issue Legal DV
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
